import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Wallet, AlertTriangle, RefreshCw, ExternalLink } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import BalanceDisplay from '../time-off/BalanceDisplay';
import api from '../../utils/api';
import { parseDate } from '../../utils/dates';
import './PtoBalanceWidget.css';

function PtoBalanceWidget() {
  const { user } = useAuth();
  const [balance, setBalance] = useState(null);
  const [ptoYear, setPtoYear] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notConfigured, setNotConfigured] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  const [w2Info, setW2Info] = useState(null);

  const fetchBalance = useCallback(async () => {
    if (!user?.id) return;
    let cancelled = false;
    try {
      setLoading(true);
      setFetchError(false);
      const data = await api.get(`/users/${user.id}/balance`);
      if (cancelled) return;

      if (data.no_balance) {
        setW2Info(data);
        setBalance(null);
        setPtoYear(null);
        setNotConfigured(false);
      } else if (data.not_configured) {
        setW2Info(null);
        setNotConfigured(true);
        setBalance(null);
        setPtoYear(null);
      } else {
        setBalance(data);
        setPtoYear(data.pto_year);
        setNotConfigured(false);
        setW2Info(null);
      }
    } catch (err) {
      if (!cancelled) {
        setBalance(null);
        setNotConfigured(false);
        setFetchError(true);
      }
    } finally {
      if (!cancelled) setLoading(false);
    }
    return () => { cancelled = true; };
  }, [user?.id]);

  useEffect(() => {
    fetchBalance();
  }, [fetchBalance]);

  // Year-end banner: check if within 60 days of the PTO year end_date (data-driven)
  const yearEndInfo = React.useMemo(() => {
    if (!ptoYear || !balance) return { show: false };
    const endDate = parseDate(ptoYear.end_date);
    if (!endDate) return { show: false };
    const now = new Date();
    const daysUntilEnd = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
    const endLabel = endDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
    return {
      show: daysUntilEnd > 0 && daysUntilEnd <= 60 && balance.available?.total > 0,
      endLabel,
    };
  }, [ptoYear, balance]);

  const daysRemaining = balance?.available?.total || 0;

  return (
    <div className="pto-balance-widget">
      <div className="pto-balance-header">
        <div className="pto-balance-title">
          <Wallet size={16} strokeWidth={2} />
          <h3>PTO Balance</h3>
        </div>
        {ptoYear && (
          <span className="pto-balance-year">{ptoYear.label}</span>
        )}
      </div>

      {loading && (
        <div className="pto-balance-loading">Loading balance...</div>
      )}

      {!loading && fetchError && (
        <div className="pto-balance-error">
          <span>Unable to load PTO balance</span>
          <button className="btn-text" onClick={fetchBalance}>
            <RefreshCw size={14} /> Retry
          </button>
        </div>
      )}

      {!loading && !fetchError && w2Info && (
        <div className="pto-balance-empty">
          PTO tracked externally -- <strong>{w2Info.days_taken_this_year || 0}</strong> day{(w2Info.days_taken_this_year || 0) === 1 ? '' : 's'} entered this year.
          {' '}
          <Link to="/time-off" className="btn-text">
            View Time Off <ExternalLink size={12} />
          </Link>
        </div>
      )}

      {!loading && !fetchError && !w2Info && notConfigured && (
        <div className="pto-balance-empty">
          No PTO allocation has been set for the current year. Your administrator will configure this.
        </div>
      )}

      {!loading && !fetchError && !notConfigured && balance && (
        <>
          <BalanceDisplay balance={balance} />

          {yearEndInfo.show && (
            <div className="pto-yearend-banner">
              <AlertTriangle size={14} />
              <span>
                You have <strong>{daysRemaining} day{daysRemaining !== 1 ? 's' : ''}</strong> remaining -- use them before {yearEndInfo.endLabel}
              </span>
            </div>
          )}
        </>
      )}

      {!loading && !fetchError && !notConfigured && !balance && (
        <div className="pto-balance-empty">No allocation for the current PTO year</div>
      )}
    </div>
  );
}

export default PtoBalanceWidget;
